import { useState, useEffect, useCallback } from 'react';
import { Table, Badge, Button, Modal, Select, Pagination } from '../../components/ui';
import { apiFetch } from '../../services/api';

const STATUS_COLORS = {
  pending:'var(--text-muted)', approved:'var(--green-light)',
  rejected:'var(--red-light)', enrolled:'var(--blue-light)',
  completed:'var(--accent)', withdrawn:'var(--text-muted)',
};
const PAYMENT_COLORS = {
  pending:'var(--yellow)', paid:'var(--green-light)',
  failed:'var(--red-light)', waived:'var(--purple-light)',
  scholarship:'var(--blue-light)',
};

const labelStyle = { fontSize:12, color:'var(--text-secondary)', fontWeight:500, textTransform:'uppercase', letterSpacing:'0.03em', display:'block', marginBottom:6 };

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:3, padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
      <div style={{ fontSize:10, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:600 }}>{label}</div>
      <div style={{ fontSize:13, color:'var(--text-primary)', lineHeight:1.6 }}>{value}</div>
    </div>
  );
}

function ApplicationTab({ e }) {
  const hasData = e.education_level || e.current_occupation || e.location || e.motivation;
  if (!hasData) return (
    <div style={{ padding:'32px 0', textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>
      <div style={{ fontSize:'2rem', opacity:0.2, marginBottom:10 }}>◈</div>
      No application data — this enrollment was submitted through the old form.
    </div>
  );
  const MEDIA_URL_ADM = process.env.REACT_APP_MEDIA_URL || 'http://localhost:5001';
  const photoSrc = e.photo_url ? (e.photo_url.startsWith('http') ? e.photo_url : `${MEDIA_URL_ADM}${e.photo_url}`) : null;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
      {/* Passport photo */}
      {photoSrc && (
        <div style={{ display:'flex', gap:16, alignItems:'center', padding:'12px 0 16px', borderBottom:'1px solid var(--border)', marginBottom:4 }}>
          <img src={photoSrc} alt="Passport" style={{ width:80, height:80, borderRadius:8, objectFit:'cover', border:'2px solid var(--accent)', flexShrink:0 }}/>
          <div>
            <div style={{ fontSize:11, fontWeight:600, color:'var(--accent)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:6 }}>Passport Photo</div>
            <a href={photoSrc} target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:'var(--accent)' }}>View full size →</a>
          </div>
        </div>
      )}
      {/* Personal */}
      <div style={{ fontSize:11, fontWeight:700, color:'var(--accent)', textTransform:'uppercase', letterSpacing:'0.1em', padding:'12px 0 6px' }}>Personal Information</div>
      <InfoRow label="Full Name"         value={e.full_name}/>
      <InfoRow label="Email"             value={e.email}/>
      <InfoRow label="Phone"             value={e.phone}/>
      <InfoRow label="Location"          value={e.location}/>
      <InfoRow label="Emergency Contact" value={e.emergency_contact}/>

      {/* Academic */}
      <div style={{ fontSize:11, fontWeight:700, color:'var(--accent)', textTransform:'uppercase', letterSpacing:'0.1em', padding:'16px 0 6px' }}>Academic Background</div>
      <InfoRow label="Education Level"    value={e.education_level}/>
      <InfoRow label="Experience Level"   value={e.experience_level}/>
      <InfoRow label="Current Occupation" value={e.current_occupation}/>
      <InfoRow label="Institution"        value={e.institution}/>
      <InfoRow label="Has Laptop"         value={e.has_laptop}/>
      <InfoRow label="Heard About Us"     value={e.hear_about_us}/>

      {/* Statement */}
      <div style={{ fontSize:11, fontWeight:700, color:'var(--accent)', textTransform:'uppercase', letterSpacing:'0.1em', padding:'16px 0 6px' }}>Personal Statement</div>
      <InfoRow label="Motivation / Why this program" value={e.motivation}/>
      <InfoRow label="Goals"                          value={e.goals}/>
      <InfoRow label="Expected Challenges"            value={e.challenges}/>
      <InfoRow label="Additional Notes"               value={e.additional_notes}/>
    </div>
  );
}

export default function EnrollmentsPage() {
  const [enrollments,  setEnrollments]  = useState([]);
  const [cohorts,      setCohorts]      = useState([]);
  const [templates,    setTemplates]    = useState([]);
  const [pagination,   setPag]          = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [page,         setPage]         = useState(1);
  const [filter,       setFilter]       = useState({ status:'', payment_status:'', cohort_id:'' });
  const [selected,     setSel]          = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [modal,        setModal]        = useState(null);
  const [modalTab,     setModalTab]     = useState('manage');
  const [bulkModal,    setBulkModal]    = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [issuing,      setIssuing]      = useState(false);
  const [bulkResult,   setBulkResult]   = useState(null);
  const [form,         setForm]         = useState({});
  const [bulkForm,     setBulkForm]     = useState({ template_id:'', expires_at:'', only_recommended:false });

  const token   = localStorage.getItem('mh_token');
  const headers = { 'Content-Type':'application/json', Authorization:`Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit:20 });
      if (filter.status)         params.set('status',         filter.status);
      if (filter.payment_status) params.set('payment_status', filter.payment_status);
      if (filter.cohort_id)      params.set('cohort_id',      filter.cohort_id);
      const res  = await apiFetch(`/admin/enrollments?${params}`, { headers });
      const data = await res.json();
      setEnrollments(data.data || []); setPag(data.pagination);
    } catch { setEnrollments([]); } finally { setLoading(false); }
  }, [page, filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    apiFetch('/programs/cohorts',              { headers }).then(r=>r.json()).then(d=>setCohorts(d.data||[])).catch(()=>{});
    apiFetch('/admin/certificate-templates',   { headers }).then(r=>r.json()).then(d=>setTemplates(d.data||[])).catch(()=>{});
  }, []);

  const openEdit = (e) => {
    setForm({
      status:           e.status,
      progress:         e.progress         || 'not_started',
      progress_pct:     e.progress_pct     || 0,
      payment_status:   e.payment_status   || 'pending',
      payment_method:   e.payment_method   || '',
      fee_amount:       e.fee_amount       || '',
      scholarship:      e.scholarship      || false,
      scholarship_note: e.scholarship_note || '',
      review_note:      e.review_note      || '',
    });
    setSel(e); setModalTab('manage'); setModal('edit');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        payment_method: form.payment_method || null,
        fee_amount:     form.fee_amount !== '' && form.fee_amount !== null && form.fee_amount !== undefined
                          ? Number(form.fee_amount) : null,
      };
      const res  = await apiFetch(`/admin/enrollments/${selected.id}`, { method:'PUT', headers, body:JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) { alert(data.error || `Save failed (${res.status})`); return; }
      setModal(null); load();
    } catch(e) { alert('Network error: ' + e.message); } finally { setSaving(false); }
  };

  const handleIssueSingle = async (enrollmentId) => {
    if (!window.confirm('Issue certificate for this enrollment?')) return;
    setIssuing(enrollmentId);
    try {
      const res  = await apiFetch(`/admin/enrollments/${enrollmentId}/certificate`, { method:'POST', headers, body:JSON.stringify({}) });
      const data = await res.json();
      if (data.data) { alert(`✓ Certificate issued — Hash: ${data.data.cert_hash?.slice(0,16)}…`); load(); }
      else alert(data.error || 'Failed');
    } catch { alert('Failed to issue certificate'); } finally { setIssuing(null); }
  };

  const handleBulkIssue = async () => {
    setSaving(true);
    try {
      const payload = {
        ...bulkForm,
        ...(filter.cohort_id   ? { cohort_id:      filter.cohort_id }      : {}),
        ...(selectedRows.size > 0 ? { enrollment_ids: [...selectedRows] } : {}),
      };
      const res  = await apiFetch('/admin/certificates/bulk', { method:'POST', headers, body:JSON.stringify(payload) });
      const data = await res.json();
      setBulkResult(data.data); load(); setSelectedRows(new Set());
    } catch { alert('Bulk issuance failed'); } finally { setSaving(false); }
  };

  const toggleRow  = (id) => { setSelectedRows(prev => { const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; }); };
  const toggleAll  = ()   => { setSelectedRows(selectedRows.size===enrollments.length?new Set():new Set(enrollments.map(e=>e.id))); };
  const eligibleCount = enrollments.filter(e=>!e.certificate_hash&&e.status!=='rejected'&&e.status!=='withdrawn').length;

  const tabBtn = (t) => ({
    padding:'8px 16px', fontSize:12, fontWeight:modalTab===t?700:400,
    color:modalTab===t?'var(--accent)':'var(--text-muted)',
    borderBottom:`2px solid ${modalTab===t?'var(--accent)':'transparent'}`,
    background:'none', border:'none', borderBottom:`2px solid ${modalTab===t?'var(--accent)':'transparent'}`,
    cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s',
  });

  const COLS = [
    { key:'_select', label:(
      <input type="checkbox" checked={selectedRows.size===enrollments.length&&enrollments.length>0} onChange={toggleAll} style={{ cursor:'pointer' }}/>
    ), render:(_,row)=>(
      <input type="checkbox" checked={selectedRows.has(row.id)} onChange={()=>toggleRow(row.id)} onClick={e=>e.stopPropagation()} style={{ cursor:'pointer' }}/>
    )},
    { key:'full_name', label:'Applicant', render:(v,row)=>(
      <div>
        <div style={{ fontWeight:500 }}>{v}</div>
        <div style={{ fontSize:11, color:'var(--text-muted)' }}>{row.email}</div>
      </div>
    )},
    { key:'program_title', label:'Program', render:(v,row)=>(
      <div>
        <div style={{ fontSize:13 }}>{v||'—'}</div>
        <div style={{ fontSize:11, color:'var(--text-muted)' }}>{row.cohort_name||'No cohort'}</div>
      </div>
    )},
    { key:'status', label:'Status', render:v=>(
      <span style={{ fontSize:11, fontWeight:600, color:STATUS_COLORS[v]||'var(--text-muted)', textTransform:'capitalize' }}>{v}</span>
    )},
    { key:'progress', label:'Progress', render:(v,row)=>(
      <div style={{ minWidth:90 }}>
        <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:3, textTransform:'capitalize' }}>{(v||'not_started').replace('_',' ')}</div>
        <div style={{ height:3, background:'var(--border)', borderRadius:2, overflow:'hidden' }}>
          <div style={{ height:'100%', width:`${row.progress_pct||0}%`, background:'var(--accent)', borderRadius:2 }}/>
        </div>
      </div>
    )},
    { key:'payment_status', label:'Payment', render:v=>(
      <span style={{ fontSize:11, fontWeight:600, color:PAYMENT_COLORS[v]||'var(--text-muted)', textTransform:'capitalize' }}>{v||'pending'}</span>
    )},
    { key:'recommend_cert', label:'Rec.', render:v=>v
      ? <span style={{ color:'var(--green-light)', fontSize:11 }}>✓</span>
      : <span style={{ color:'var(--text-muted)', fontSize:11 }}>—</span>
    },
    { key:'certificate_hash', label:'Cert', render:v=>v
      ? <span style={{ fontSize:10, color:'var(--green-light)' }}>✓ Issued</span>
      : <span style={{ fontSize:10, color:'var(--text-muted)' }}>—</span>
    },
    { key:'_actions', label:'', render:(_,row)=>(
      <div style={{ display:'flex', gap:6 }} onClick={e=>e.stopPropagation()}>
        <Button variant="ghost" size="sm" onClick={()=>openEdit(row)}>Manage</Button>
        {!row.certificate_hash && row.status!=='rejected' && (
          <Button variant="secondary" size="sm" loading={issuing===row.id} onClick={()=>handleIssueSingle(row.id)}>Issue</Button>
        )}
      </div>
    )},
  ];

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:24 }}>

      {/* Toolbar */}
      <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap', justifyContent:'space-between' }}>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          <Select value={filter.cohort_id} onChange={e=>setFilter(f=>({...f,cohort_id:e.target.value}))} style={{ width:220 }}>
            <option value="">All Cohorts</option>
            {cohorts.map(c=><option key={c.id} value={c.id}>{c.program_title} — {c.cohort_name}</option>)}
          </Select>
          <Select value={filter.status} onChange={e=>setFilter(f=>({...f,status:e.target.value}))} style={{ width:150 }}>
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="enrolled">Enrolled</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
          </Select>
          <Select value={filter.payment_status} onChange={e=>setFilter(f=>({...f,payment_status:e.target.value}))} style={{ width:160 }}>
            <option value="">All Payments</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="waived">Waived</option>
            <option value="scholarship">Scholarship</option>
          </Select>
          <span style={{ fontSize:12, color:'var(--text-muted)', alignSelf:'center' }}>{pagination?.total??0} enrollments</span>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {selectedRows.size > 0 && (
            <Button variant="secondary" onClick={()=>{setBulkForm({template_id:'',expires_at:'',only_recommended:false});setBulkModal(true);setBulkResult(null);}}>
              ◈ Issue Certs ({selectedRows.size} selected)
            </Button>
          )}
          {filter.cohort_id && selectedRows.size===0 && eligibleCount>0 && (
            <Button variant="secondary" onClick={()=>{setBulkForm({template_id:'',expires_at:'',only_recommended:false});setBulkModal(true);setBulkResult(null);}}>
              ◈ Bulk Issue for Cohort ({eligibleCount} eligible)
            </Button>
          )}
        </div>
      </div>

      {selectedRows.size > 0 && (
        <div style={{ padding:'10px 16px', background:'rgba(232,164,58,0.08)', border:'1px solid rgba(232,164,58,0.25)', borderRadius:8, display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:13 }}>
          <span style={{ color:'var(--accent)' }}>{selectedRows.size} enrollment{selectedRows.size>1?'s':''} selected</span>
          <button onClick={()=>setSelectedRows(new Set())} style={{ fontSize:12, color:'var(--text-muted)', background:'none', border:'none', cursor:'pointer' }}>Clear selection</button>
        </div>
      )}

      <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
        <Table columns={COLS} data={enrollments} loading={loading} onRowClick={openEdit}/>
        <Pagination pagination={pagination} onPage={setPage}/>
      </div>

      {/* Manage + Application modal */}
      <Modal open={modal==='edit'} onClose={()=>setModal(null)} title={`${selected?.full_name}`} width={640}>
        {selected && (
          <div style={{ display:'flex', flexDirection:'column', gap:0 }}>

            {/* Tab bar */}
            <div style={{ display:'flex', borderBottom:'1px solid var(--border)', marginBottom:20, gap:0 }}>
              <button style={tabBtn('manage')}    onClick={()=>setModalTab('manage')}>Manage Enrollment</button>
              <button style={tabBtn('application')} onClick={()=>setModalTab('application')}>
                Application Data
                {(selected.motivation||selected.education_level) && (
                  <span style={{ marginLeft:6, fontSize:10, padding:'2px 6px', borderRadius:10, background:'rgba(62,207,142,0.15)', color:'#3ecf8e' }}>✓</span>
                )}
              </button>
            </div>

            {/* MANAGE TAB */}
            {modalTab==='manage' && (
              <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                {/* Summary row */}
                <div style={{ background:'var(--bg-elevated)', borderRadius:'var(--radius-md)', padding:'12px 16px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  {[
                    ['Program', selected.program_title||'—'],
                    ['Cohort',  selected.cohort_name||'No cohort'],
                    ['Email',   selected.email],
                    ['Applied', new Date(selected.created_at).toLocaleDateString('en-RW')],
                  ].map(([k,v])=>(
                    <div key={k}>
                      <div style={{ fontSize:10, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:2 }}>{k}</div>
                      <div style={{ fontSize:12 }}>{v}</div>
                    </div>
                  ))}
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                  <Select label="Status" value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="enrolled">Enrolled</option>
                    <option value="completed">Completed</option>
                    <option value="rejected">Rejected</option>
                    <option value="withdrawn">Withdrawn</option>
                  </Select>
                  <Select label="Progress" value={form.progress} onChange={e=>setForm(f=>({...f,progress:e.target.value}))}>
                    <option value="not_started">Not Started</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="dropped">Dropped</option>
                    <option value="on_hold">On Hold</option>
                  </Select>
                  <div style={{ gridColumn:'1/-1' }}>
                    <label style={labelStyle}>Progress % ({form.progress_pct}%)</label>
                    <input type="range" min={0} max={100} value={form.progress_pct}
                      onChange={e=>setForm(f=>({...f,progress_pct:parseInt(e.target.value)}))}
                      style={{ width:'100%', accentColor:'var(--accent)' }}/>
                  </div>
                  <Select label="Payment Status" value={form.payment_status} onChange={e=>setForm(f=>({...f,payment_status:e.target.value}))}>
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="failed">Failed</option>
                    <option value="waived">Waived</option>
                    <option value="scholarship">Scholarship</option>
                  </Select>
                  <Select label="Payment Method" value={form.payment_method} onChange={e=>setForm(f=>({...f,payment_method:e.target.value}))}>
                    <option value="">— Select —</option>
                    <option value="mtn_momo">MTN MoMo</option>
                    <option value="stripe">Stripe / Card</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cash">Cash</option>
                    <option value="scholarship">Scholarship</option>
                    <option value="waived">Waived</option>
                  </Select>
                  <div style={{ gridColumn:'1/-1' }}>
                    <label style={labelStyle}>Review Note</label>
                    <textarea value={form.review_note} onChange={e=>setForm(f=>({...f,review_note:e.target.value}))} rows={2}
                      style={{ width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'9px 12px', fontSize:13, resize:'vertical', fontFamily:'inherit', boxSizing:'border-box' }}/>
                  </div>
                </div>

                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <div>
                    {!selected.certificate_hash && (
                      <Button variant="secondary" loading={issuing===selected.id}
                        onClick={()=>{setModal(null);handleIssueSingle(selected.id);}}>
                        Issue Certificate
                      </Button>
                    )}
                  </div>
                  <div style={{ display:'flex', gap:8 }}>
                    <Button variant="secondary" onClick={()=>setModal(null)}>Cancel</Button>
                    <Button onClick={handleSave} loading={saving}>Save Changes</Button>
                  </div>
                </div>
              </div>
            )}

            {/* APPLICATION TAB */}
            {modalTab==='application' && (
              <div style={{ maxHeight:480, overflowY:'auto', paddingRight:4 }}>
                <ApplicationTab e={selected}/>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Bulk issuance modal */}
      <Modal open={bulkModal} onClose={()=>{setBulkModal(false);setBulkResult(null);}}
        title={selectedRows.size>0?`Issue Certificates — ${selectedRows.size} selected`:`Bulk Issue — ${filter.cohort_id?cohorts.find(c=>c.id===filter.cohort_id)?.cohort_name||'Cohort':'All Eligible'}`}
        width={520}>
        {bulkResult ? (
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div style={{ padding:'20px', background:'rgba(62,207,142,0.08)', border:'1px solid rgba(62,207,142,0.3)', borderRadius:10 }}>
              <div style={{ fontFamily:'var(--font-display)', fontSize:'1.3rem', fontWeight:700, color:'#3ecf8e', marginBottom:12 }}>✓ Bulk Issuance Complete</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                {[['Issued',bulkResult.issued,'var(--green-light)'],['Failed',bulkResult.failed,'var(--red-light)'],['Total',bulkResult.total,'var(--accent)']].map(([l,v,c])=>(
                  <div key={l} style={{ padding:'12px', background:'var(--bg-elevated)', borderRadius:8 }}>
                    <div style={{ fontSize:10, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>{l}</div>
                    <div style={{ fontFamily:'var(--font-display)', fontSize:'1.6rem', fontWeight:700, color:c }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
            {bulkResult.failures?.length > 0 && (
              <div style={{ background:'rgba(240,96,96,0.08)', border:'1px solid rgba(240,96,96,0.3)', borderRadius:8, padding:'12px 16px' }}>
                <div style={{ fontSize:12, fontWeight:600, color:'var(--red-light)', marginBottom:8 }}>Failures</div>
                {bulkResult.failures.map((f,i)=><div key={i} style={{ fontSize:11, color:'var(--text-muted)', marginBottom:4 }}>{f.name} — {f.error}</div>)}
              </div>
            )}
            <Button onClick={()=>{setBulkModal(false);setBulkResult(null);}} style={{ justifyContent:'center' }}>Done</Button>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div style={{ padding:'12px 16px', background:'rgba(232,164,58,0.08)', border:'1px solid rgba(232,164,58,0.25)', borderRadius:8, fontSize:12, color:'var(--text-secondary)', lineHeight:1.6 }}>
              {selectedRows.size>0
                ?`Will issue certificates to the ${selectedRows.size} selected enrollment${selectedRows.size>1?'s':''}. Enrollments that already have a certificate will be skipped.`
                :`Will issue certificates to all eligible enrollments${filter.cohort_id?' in this cohort':''}. Enrollments that already have a certificate will be skipped.`
              }
            </div>
            <Select label="Certificate Template (optional override)" value={bulkForm.template_id}
              onChange={e=>setBulkForm(f=>({...f,template_id:e.target.value}))}>
              <option value="">— Auto-resolve by program type —</option>
              {templates.map(t=><option key={t.id} value={t.id}>{t.name}{t.is_default?' (Default)':''}</option>)}
            </Select>
            <div>
              <label style={labelStyle}>Expiry Date (optional)</label>
              <input type="date" value={bulkForm.expires_at} onChange={e=>setBulkForm(f=>({...f,expires_at:e.target.value}))}
                style={{ width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'8px 12px', fontSize:13, boxSizing:'border-box' }}/>
            </div>
            {!selectedRows.size && filter.cohort_id && (
              <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', fontSize:13 }}>
                <input type="checkbox" checked={bulkForm.only_recommended}
                  onChange={e=>setBulkForm(f=>({...f,only_recommended:e.target.checked}))}/>
                <span style={{ color:'var(--text-secondary)' }}>
                  Only issue to trainer-recommended students
                  <span style={{ color:'var(--text-muted)', marginLeft:6, fontSize:11 }}>(recommend_cert = true)</span>
                </span>
              </label>
            )}
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <Button variant="secondary" onClick={()=>setBulkModal(false)}>Cancel</Button>
              <Button onClick={handleBulkIssue} loading={saving}>Issue Certificates</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
